import { useState } from 'react'
import { useCurrentMetrics, useMetricsHistory } from '@/hooks/useMonitoring'
import type { NodeMetrics, MetricPoint } from '@/api/monitor'
import { Button, Card, EmptyState } from '@/admin-design/_common'

// GaugeBar uses ordinal threshold colors (>90% red / >70% amber / <70% green)
// — these are data-viz, KEPT RAW per project conventions.
function GaugeBar({
  label,
  value,
  max,
  format,
}: {
  label: string
  value: number
  max: number
  format?: (v: number, m: number) => string
}) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  const color =
    pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'
  const display = format ? format(value, max) : `${pct.toFixed(1)}%`

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-foreground">{display}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return `${gb.toFixed(1)} GB`
  const mb = bytes / (1024 * 1024)
  return `${mb.toFixed(0)} MB`
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${days}d ${hours}h`
  const mins = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${mins}m`
}

function NodeMetricsCard({ metrics }: { metrics: NodeMetrics }) {
  const sys = metrics.system
  const memPct =
    sys.memory_total_bytes > 0
      ? (sys.memory_used_bytes / sys.memory_total_bytes) * 100
      : 0
  const diskPct =
    sys.disk_total_bytes > 0
      ? (sys.disk_used_bytes / sys.disk_total_bytes) * 100
      : 0

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-card-foreground">{metrics.node_name}</h3>
        {sys.uptime_seconds > 0 && (
          <span className="text-[10px] text-muted-foreground">
            uptime: {formatUptime(sys.uptime_seconds)}
          </span>
        )}
      </div>

      <GaugeBar label="CPU" value={sys.cpu_percent} max={100} />

      <GaugeBar
        label="Memory"
        value={memPct}
        max={100}
        format={() =>
          `${formatBytes(sys.memory_used_bytes)} / ${formatBytes(sys.memory_total_bytes)} (${memPct.toFixed(1)}%)`
        }
      />

      <GaugeBar
        label="Disk"
        value={diskPct}
        max={100}
        format={() =>
          `${formatBytes(sys.disk_used_bytes)} / ${formatBytes(sys.disk_total_bytes)} (${diskPct.toFixed(1)}%)`
        }
      />

      {sys.load_avg_1 > 0 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Load Avg</span>
          <span className="font-mono text-foreground">
            {sys.load_avg_1.toFixed(2)} / {sys.load_avg_5.toFixed(2)} /{' '}
            {sys.load_avg_15.toFixed(2)}
          </span>
        </div>
      )}

      {/* GPU metrics (GPU node only) */}
      {metrics.gpu && (
        <div className="border-t border-border pt-3 mt-3 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium text-foreground">GPU</span>
            <span className="text-muted-foreground">{metrics.gpu.name}</span>
          </div>
          <GaugeBar
            label="GPU Usage"
            value={metrics.gpu.utilization_gpu_pct}
            max={100}
          />
          <GaugeBar
            label="VRAM"
            value={metrics.gpu.memory_used_mb}
            max={metrics.gpu.memory_total_mb}
            format={(v, m) => `${v} MB / ${m} MB`}
          />
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Temp / Power</span>
            <span className="font-mono text-foreground">
              {metrics.gpu.temperature_c}C | {metrics.gpu.power_draw_w}W /{' '}
              {metrics.gpu.power_limit_w}W
            </span>
          </div>
        </div>
      )}

      {/* Containers */}
      {metrics.containers && metrics.containers.length > 0 && (
        <div className="border-t border-border pt-3 mt-3">
          <h4 className="text-xs font-medium text-foreground mb-2">
            Containers ({metrics.containers.length})
          </h4>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {metrics.containers.map((c) => (
              <div
                key={c.name}
                className="flex items-center justify-between text-xs"
              >
                <span className="text-foreground truncate mr-2">{c.name}</span>
                <span
                  className={`shrink-0 font-medium ${
                    c.state === 'running'
                      ? 'text-success'
                      : c.state === 'exited'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                  }`}
                >
                  {c.state}
                  {c.error_count_24h > 0 && (
                    <span className="ml-1 text-destructive/80">
                      ({c.error_count_24h} err)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

function MiniChart({
  points,
  field,
  label,
}: {
  points: MetricPoint[]
  field: (p: MetricPoint) => number
  label: string
}) {
  if (points.length === 0) return null

  const values = points.map(field)
  const max = Math.max(...values, 1)
  const h = 60
  const w = 300

  const pathPoints = values.map((v, i) => {
    const x = (i / Math.max(values.length - 1, 1)) * w
    const y = h - (v / max) * h
    return `${x},${y}`
  })
  const pathD = `M ${pathPoints.join(' L ')}`

  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono text-muted-foreground">
          max: {max.toFixed(1)}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full h-16 border border-border rounded bg-muted/50"
        preserveAspectRatio="none"
      >
        <path
          d={pathD}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-blue-500"
        />
      </svg>
    </div>
  )
}

export default function Monitoring() {
  const {
    data: metrics,
    isLoading,
    isError,
    isFetching,
  } = useCurrentMetrics()
  const [historyNode, setHistoryNode] = useState<string>('mini')
  const { data: history } = useMetricsHistory(historyNode, 24)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Monitoring</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Real-time system metrics and container health
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            refreshing...
          </span>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Card key={i} className="h-48 animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="border border-destructive-soft rounded-lg bg-destructive-soft p-4">
          <p className="text-sm text-destructive-soft-foreground">
            Failed to load monitoring data. Check that the backend is running.
          </p>
        </div>
      )}

      {/* Current metrics */}
      {!isLoading && !isError && metrics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...metrics].sort((a, b) => {
            // mini first (left), gpu second (right)
            if (a.gpu && !b.gpu) return 1
            if (!a.gpu && b.gpu) return -1
            return a.node_name.localeCompare(b.node_name)
          }).map((nm: NodeMetrics) => (
            <NodeMetricsCard key={nm.node_name} metrics={nm} />
          ))}
        </div>
      )}

      {/* History */}
      <Card className="p-4 space-y-4">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-card-foreground">
            24h History
          </h2>
          <div className="flex gap-1">
            {['mini', 'gpu'].map((n) => (
              <Button
                key={n}
                type="button"
                variant={historyNode === n ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setHistoryNode(n)}
                className="h-auto px-2 py-1 text-xs shadow-none"
              >
                {n}
              </Button>
            ))}
          </div>
        </div>

        {history && history.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MiniChart
              points={history}
              field={(p) => p.system.cpu_percent}
              label="CPU %"
            />
            <MiniChart
              points={history}
              field={(p) =>
                p.system.memory_total_bytes > 0
                  ? (p.system.memory_used_bytes /
                      p.system.memory_total_bytes) *
                    100
                  : 0
              }
              label="Memory %"
            />
            <MiniChart
              points={history}
              field={(p) => p.system.load_avg_1}
              label="Load Avg (1m)"
            />
            <MiniChart
              points={history}
              field={(p) =>
                p.system.disk_total_bytes > 0
                  ? (p.system.disk_used_bytes / p.system.disk_total_bytes) *
                    100
                  : 0
              }
              label="Disk %"
            />
          </div>
        ) : (
          <EmptyState
            description="No history data available yet. Metrics are collected every 5 minutes."
            className="py-4"
          />
        )}
      </Card>
    </div>
  )
}
